"use strict";
// Copyright (c) 2024-2026 The Bitcoin developers
// Distributed under the MIT software license, see the accompanying
// file COPYING or http://www.opensource.org/licenses/mit-license.php.
Object.defineProperty(exports, "__esModule", { value: true });
exports.P2PKSignatory = exports.P2SHMultisigSignatorySchnorr = exports.P2SHMultisigSignatory = exports.BareMultisigSignatorySchnorr = exports.BareMultisigSignatory = exports.P2PKHSignatory = void 0;
exports.flagSignature = flagSignature;
exports.signWithSigHash = signWithSigHash;
/**
 * Signatories and signing helpers used with `TxBuilder` (`TxBuilderInput.signatory`).
 */
const ecc_js_1 = require("./ecc.js");
const consts_js_1 = require("./consts.js");
const hash_js_1 = require("./hash.js");
const writerbytes_js_1 = require("./io/writerbytes.js");
const op_js_1 = require("./op.js");
const script_js_1 = require("./script.js");
const sigHashType_js_1 = require("./sigHashType.js");
/** Append the sighash flags to the signature */
function flagSignature(sig, sigHashFlags) {
    const writer = new writerbytes_js_1.WriterBytes(sig.length + 1);
    writer.putBytes(sig);
    writer.putU8(sigHashFlags.toInt() & 0xff);
    return writer.data;
}
/**
 * Sign the sighash using Schnorr for BIP143 signatures and ECDSA for Legacy
 * signatures, and then flags the signature correctly
 **/
function signWithSigHash(ecc, sk, sigHash, sigHashType) {
    const sig = sigHashType.variant == sigHashType_js_1.SigHashTypeVariant.LEGACY
        ? ecc.ecdsaSign(sk, sigHash)
        : ecc.schnorrSign(sk, sigHash);
    return flagSignature(sig, sigHashType);
}
/** Signatory for a P2PKH input. Always uses Schnorr signatures */
const P2PKHSignatory = (sk, pk, sigHashType) => {
    return (ecc, input) => {
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sigFlagged = signWithSigHash(ecc, sk, sighash, sigHashType);
        return script_js_1.Script.p2pkhSpend(pk, sigFlagged);
    };
};
exports.P2PKHSignatory = P2PKHSignatory;
/** Signatory for bare m-of-n multisig (output script is the multisig script itself). */
const BareMultisigSignatory = (m, pubkeys, sk, myPk, sigHashType) => {
    return (ecc, input) => {
        if (ecc instanceof ecc_js_1.EccDummy) {
            const dummySig = new Uint8Array(consts_js_1.ECDSA_SIG_ESTIMATE_BYTES);
            const signatures = Array(m)
                .fill(undefined)
                .map(() => dummySig);
            return script_js_1.Script.multisigSpend({ signatures });
        }
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sig = ecc.ecdsaSign(sk, sighash);
        const sigFlagged = flagSignature(sig, sigHashType);
        const myIndex = pubkeys.findIndex(pk => pk.length === myPk.length && pk.every((b, i) => b === myPk[i]));
        if (myIndex < 0) {
            throw new Error('Signer pubkey not found in multisig pubkeys');
        }
        const n = pubkeys.length;
        const signatures = Array(n).fill(undefined);
        signatures[myIndex] = sigFlagged;
        const nonNull = signatures.filter((s) => s !== undefined);
        const sigsForScript = [
            ...nonNull,
            ...Array(m - nonNull.length).fill(undefined),
        ];
        return script_js_1.Script.multisigSpend({ signatures: sigsForScript });
    };
};
exports.BareMultisigSignatory = BareMultisigSignatory;
/** Signatory for bare m-of-n multisig using Schnorr signatures (BIP143 Schnorr multisig). */
const BareMultisigSignatorySchnorr = (m, pubkeys, sk, myPk, signerIndices, sigHashType) => {
    return (ecc, input) => {
        const n = pubkeys.length;
        if (signerIndices.size !== m) {
            throw new Error(`signerIndices must have ${m} elements for ${m}-of-${n} multisig`);
        }
        if (ecc instanceof ecc_js_1.EccDummy) {
            const dummySig = new Uint8Array(consts_js_1.SCHNORR_SIG_ESTIMATE_BYTES);
            const signatures = Array(m)
                .fill(undefined)
                .map(() => dummySig);
            return script_js_1.Script.multisigSpend({
                signatures,
                pubkeyIndices: signerIndices,
                numPubkeys: n,
            });
        }
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sig = ecc.schnorrSign(sk, sighash);
        const sigFlagged = flagSignature(sig, sigHashType);
        const myIndex = pubkeys.findIndex(pk => pk.length === myPk.length && pk.every((b, i) => b === myPk[i]));
        if (myIndex < 0) {
            throw new Error('Signer pubkey not found in multisig pubkeys');
        }
        if (!signerIndices.has(myIndex)) {
            throw new Error(`Signer index ${myIndex} not in signerIndices ${[
                ...signerIndices,
            ].join(',')}`);
        }
        const sortedIndices = [...signerIndices].sort((a, b) => a - b);
        const signatures = sortedIndices.map(idx => idx === myIndex ? sigFlagged : undefined);
        return script_js_1.Script.multisigSpend({
            signatures,
            pubkeyIndices: signerIndices,
            numPubkeys: n,
        });
    };
};
exports.BareMultisigSignatorySchnorr = BareMultisigSignatorySchnorr;
/** Signatory for P2SH m-of-n multisig. */
const P2SHMultisigSignatory = (m, pubkeys, sk, myPk, sigHashType) => {
    return (ecc, input) => {
        const redeemScript = script_js_1.Script.multisig(m, pubkeys);
        if (ecc instanceof ecc_js_1.EccDummy) {
            const dummySig = new Uint8Array(consts_js_1.ECDSA_SIG_ESTIMATE_BYTES);
            const signatures = Array(m)
                .fill(undefined)
                .map(() => dummySig);
            return script_js_1.Script.multisigSpend({
                signatures,
                redeemScript,
            });
        }
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sig = ecc.ecdsaSign(sk, sighash);
        const sigFlagged = flagSignature(sig, sigHashType);
        const myIndex = pubkeys.findIndex(pk => pk.length === myPk.length && pk.every((b, i) => b === myPk[i]));
        if (myIndex < 0) {
            throw new Error('Signer pubkey not found in multisig pubkeys');
        }
        const n = pubkeys.length;
        const signatures = Array(n).fill(undefined);
        signatures[myIndex] = sigFlagged;
        const nonNull = signatures.filter((s) => s !== undefined);
        const sigsForScript = [
            ...nonNull,
            ...Array(m - nonNull.length).fill(undefined),
        ];
        return script_js_1.Script.multisigSpend({
            signatures: sigsForScript,
            redeemScript,
        });
    };
};
exports.P2SHMultisigSignatory = P2SHMultisigSignatory;
/** Signatory for P2SH m-of-n multisig using Schnorr signatures. */
const P2SHMultisigSignatorySchnorr = (m, pubkeys, sk, myPk, signerIndices, sigHashType) => {
    return (ecc, input) => {
        const redeemScript = script_js_1.Script.multisig(m, pubkeys);
        const n = pubkeys.length;
        if (signerIndices.size !== m) {
            throw new Error(`signerIndices must have ${m} elements for ${m}-of-${n} multisig`);
        }
        if (ecc instanceof ecc_js_1.EccDummy) {
            const dummySig = new Uint8Array(consts_js_1.SCHNORR_SIG_ESTIMATE_BYTES);
            const signatures = Array(m)
                .fill(undefined)
                .map(() => dummySig);
            return script_js_1.Script.multisigSpend({
                signatures,
                redeemScript,
                pubkeyIndices: signerIndices,
            });
        }
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sig = ecc.schnorrSign(sk, sighash);
        const sigFlagged = flagSignature(sig, sigHashType);
        const myIndex = pubkeys.findIndex(pk => pk.length === myPk.length && pk.every((b, i) => b === myPk[i]));
        if (myIndex < 0) {
            throw new Error('Signer pubkey not found in multisig pubkeys');
        }
        if (!signerIndices.has(myIndex)) {
            throw new Error(`Signer index ${myIndex} not in signerIndices ${[
                ...signerIndices,
            ].join(',')}`);
        }
        const sortedIndices = [...signerIndices].sort((a, b) => a - b);
        const signatures = sortedIndices.map(idx => idx === myIndex ? sigFlagged : undefined);
        return script_js_1.Script.multisigSpend({
            signatures,
            redeemScript,
            pubkeyIndices: signerIndices,
        });
    };
};
exports.P2SHMultisigSignatorySchnorr = P2SHMultisigSignatorySchnorr;
/** Signatory for a P2PK input. Always uses Schnorr signatures */
const P2PKSignatory = (sk, sigHashType) => {
    return (ecc, input) => {
        const preimage = input.sigHashPreimage(sigHashType);
        const sighash = (0, hash_js_1.sha256d)(preimage.bytes);
        const sigFlagged = signWithSigHash(ecc, sk, sighash, sigHashType);
        return script_js_1.Script.fromOps([(0, op_js_1.pushBytesOp)(sigFlagged)]);
    };
};
exports.P2PKSignatory = P2PKSignatory;
//# sourceMappingURL=signatories.js.map